/**
 * AppointmentsManag.jsx — Al Barede Law Firm
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin Dashboard to view and manage law office appointments.
 * Displays appointments as flexible, clean, and modern informative cards.
 */

import './AppointmentsManag.css';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// const DEFAULT_API = 'http://localhost/server/testServer';
const DEFAULT_API = 'https://www.albarede.com/server'
function AppointmentsManag({ apiBase = DEFAULT_API }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | pending | confirmed | cancelled | completed
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  // ─── جلب المواعيد من السيرفر ───────────────────────────────────────────────
  const fetchAppointments = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${apiBase}/get_admin_appointments.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setAppointments(d.data);
        } else {
          setError(d.error || 'Failed to load appointments.');
        }
      })
      .catch(() => setError('Network error loading dashboard data.'))
      .finally(() => setLoading(false));
  }, [apiBase]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // ─── تحديث حالة الموعد تِبعاً للسيرفر ──────────────────────────────────────────
  const handleStatusUpdate = (appointmentId, newStatus) => {
    fetch(`${apiBase}/update_appointment_status.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointment_id: appointmentId,
        status: newStatus
      })
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          fetchAppointments(); // تحديث فوري بعد النجاح
        } else {
          alert(data.error || 'Failed to update status.');
        }
      })
      .catch(() => alert('Network error updating status.'));
  };

  // ─── تنسيق الوقت والتاريخ للعرض ───────────────────────────────────────────
  const fmtDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fmtTime = (t) => {
    const [hh, mm] = t.split(':');
    const h = parseInt(hh, 10);
    return `${h > 12 ? h - 12 : h || 12}:${mm} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  // ─── الفلترة والبحث داخل المصفوفة ──────────────────────────────────────────
  const filteredAppointments = appointments.filter((appt) => {
    const matchesStatus = filterStatus === 'all' || appt.status === filterStatus;
    
    const fullName = `${appt.client_first_name} ${appt.client_last_name}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      appt.client_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (appt.confirmation_token && appt.confirmation_token.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesStatus && matchesSearch;
  });

  return (
    <div className="abd-wrap">
      {/* الهيدر العلوي للوحة التحكم */}
      <header className="abd-header">
        <button className="abd-refresh-btn" onClick={fetchAppointments} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </header>

      {/* العدادات الإحصائية السريعة */}
      <section className="abd-stats-grid">
        <div className="abd-stat-card">
          <span className="lbl">Total Bookings</span>
          <span className="val">{appointments.length}</span>
        </div>
        <div className="abd-stat-card pending">
          <span className="lbl">Pending Action</span>
          <span className="val">{appointments.filter(a => a.status === 'pending').length}</span>
        </div>
        <div className="abd-stat-card confirmed">
          <span className="lbl">Confirmed</span>
          <span className="val">{appointments.filter(a => a.status === 'confirmed').length}</span>
        </div>
      </section>

      {/* أدوات الفلترة والبحث */}
      <div className="abd-controls">
        <div className="abd-search-box">
          <input
            type="text"
            placeholder="Search by client name, email, or token..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="abd-filter-tabs">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              className={`abd-tab-btn ${filterStatus === status ? 'active' : ''}`}
              onClick={() => setFilterStatus(status)}
            >
              {status.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* عرض الأخطاء أو مؤشر التحميل */}
      {error && <div className="abd-error-msg">{error}</div>}

      {loading ? (
        <div className="abd-loading-box">
          <div className="abd-spinner" />
          <p>Loading appointments from secure server...</p>
        </div>
      ) : (
        <>
          {filteredAppointments.length === 0 ? (
            <div className="abd-cards-empty">
              No appointments found matching the selected criteria.
            </div>
          ) : (
            /* شبكة البطاقات الجديدة المتجاوبة */
            <div className="abd-cards-grid">
              {filteredAppointments.map((appt) => (
                <div key={appt.id} className={`abd-appointment-card border-${appt.status}`}>
                  
                  {/* رأس البطاقة: يحتوي على التوكن والحالة الحالية */}
                  <div className="abd-card-header">
                    <span className="abd-card-token" title={appt.confirmation_token}>
                      Token: <code>{appt.confirmation_token.substring(0, 8)}...</code>
                    </span>
                    <span className={`abd-status-badge ${appt.status}`}>
                      {appt.status}
                    </span>
                  </div>

                  {/* تفاصيل نوع الاستشارة */}
                  <div className="abd-card-type">
                    <h5>{appt.consultation_type_name || 'General Consultation'}</h5>
                  </div>

                  {/* بيانات العميل */}
                  <div className="abd-card-body">
                    <div className="abd-client-info">
                      <span className="client-name">{appt.client_first_name} {appt.client_last_name}</span>
                      <span className="client-meta">{appt.client_email}</span>
                      {appt.client_phone && <span className="client-meta">📞 {appt.client_phone}</span>}
                    </div>

                    {/* التوقيت والتاريخ الحجز */}
                    <div className="abd-schedule-info">
                      <div className="sched-item">
                        <span className="sched-lbl">Date:</span>
                        <span className="sched-val">{fmtDate(appt.appointment_date)}</span>
                      </div>
                      <div className="sched-item">
                        <span className="sched-lbl">Time:</span>
                        <span className="sched-val">{fmtTime(appt.appointment_time)}</span>
                      </div>
                    </div>

                    {/* الملاحظات إن وجدت */}
                    {appt.notes && (
                      <div className="abd-card-notes">
                        <strong>Notes:</strong> {appt.notes}
                      </div>
                    )}
                  </div>

                  {/* أسفل البطاقة: أزرار اتخاذ الإجراء السريع */}
                  <div className="abd-card-footer">
                    <span className="submitted-at">Booked: {new Date(appt.created_at).toLocaleDateString()}</span>
                    
                    <div className="abd-card-actions">
                      {appt.status === 'pending' && (
                        <>
                          <button 
                            className="abd-card-btn btn-confirm"
                            onClick={() => handleStatusUpdate(appt.id, 'confirmed')}
                          >
                            Confirm
                          </button>
                          <button 
                            className="abd-card-btn btn-cancel"
                            onClick={() => handleStatusUpdate(appt.id, 'cancelled')}
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      {appt.status === 'confirmed' && (
                        <button 
                          className="abd-card-btn btn-complete"
                          onClick={() => handleStatusUpdate(appt.id, 'completed')}
                        >
                          Mark Done
                        </button>
                      )}

                      {(appt.status === 'cancelled' || appt.status === 'completed') && (
                        <span className="action-completed-lbl">—</span>
                      )}
                    </div>
                  </div>

                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AppointmentsManag;